import type { ComponentChildren } from 'preact';
import { Link } from '../Link.tsx';
import { MarketingFooter } from '../marketing/MarketingFooter.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useLocale } from '../../hooks/useLocale.tsx';
import { useT } from '../../i18n.ts';
import type { BlogPostMeta } from '../../pages/blog/posts.ts';
import '../../styles/marketing.css';

interface BlogArticleProps {
  post: BlogPostMeta;
  children: ComponentChildren;
}

// An ISO calendar date rendered in the reader's locale. The date carries no
// time or zone, so it is pinned to UTC on both sides: parsing it as a UTC
// instant and formatting it in UTC shows the same calendar day everywhere.
export function useFormatPublishedOn(): (isoDate: string) => string {
  const { locale } = useLocale();
  return (isoDate) =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }).format(
      new Date(`${isoDate}T00:00:00Z`),
    );
}

// The shared shape of every blog post: an eyebrow back to the index, the title,
// the publication date, and the post's prose in one readable column over the
// site footer. Posts render inside this and supply only their body.
export function BlogArticle({ post, children }: BlogArticleProps) {
  const t = useT();
  const title = t(post.titleKey);
  const formatDate = useFormatPublishedOn();
  useDocumentTitle(title);

  return (
    <div className="mkt-page">
      <section className="mkt-section">
        <div className="mkt-container-narrow">
          <header className="blog-head" data-gp-name="post-head">
            <Link to="/blog" className="mkt-eyebrow blog-eyebrow">
              {t('blog.eyebrow')}
            </Link>
            <h1 className="blog-title">{title}</h1>
            <p className="blog-dateline">
              <time dateTime={post.publishedOn}>{formatDate(post.publishedOn)}</time>
            </p>
          </header>
          <article className="mkt-doc-prose blog-body" data-gp-name="post-body">
            {children}
          </article>
          <p className="blog-foot" data-gp-name="post-foot">
            <Link to="/blog" className="mkt-inline-link">
              {t('blog.all-posts')}
            </Link>
          </p>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
